import React from "react";
import { Clock } from "lucide-react";

/**
 * PreShowOverlay — pre-show countdown panel that sits OVER the closed curtains.
 *
 * Used during the OFF AIR / station-break state to show the user what's coming
 * up next and exactly when the curtains will rise. Replaces the previous OFF
 * AIR card that was hidden behind the closed curtains.
 *
 * Props:
 *   - channelName: string  e.g. "GCR Action"
 *   - nextTitle:   string  e.g. "Avatar: The Way of Water"
 *   - nextPoster:  string  optional poster URL (faded in behind the countdown)
 *   - seconds:     number  remaining seconds until curtains rise (passed in already-computed)
 *   - onRefresh:   () => void
 *   - onGuide:     () => void
 */
function fmt(s) {
  if (s <= 0) return "00:00";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export default function PreShowOverlay({
  channelName,
  nextTitle,
  nextPoster,
  seconds = 0,
  onRefresh,
  onGuide,
}) {
  // Last 10 seconds: amber-glow pulse. Last 3: tighter pulse + larger digits.
  const finalRush = seconds > 0 && seconds <= 10;
  const lastBreath = seconds > 0 && seconds <= 3;
  return (
    <div
      data-testid="theater-preshow"
      className="relative w-full text-center"
    >
      {/* Faded poster behind the countdown (gives the pre-show panel cinematic depth) */}
      {nextPoster && (
        <div
          aria-hidden="true"
          className="absolute -inset-x-24 -inset-y-12 -z-10 opacity-[0.18] blur-md"
          style={{
            backgroundImage: `url(${nextPoster})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "radial-gradient(circle, black 0%, black 50%, transparent 80%)",
            WebkitMaskImage: "radial-gradient(circle, black 0%, black 50%, transparent 80%)",
          }}
        />
      )}

      <div className="inline-flex items-center gap-2 bg-[#f6c34a]/10 border border-[#f6c34a]/30 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.3em] font-bold text-[#f6c34a] mb-5">
        <Clock className="w-3 h-3" /> Next up on {channelName || "this channel"}
      </div>

      {nextTitle && (
        <h2
          data-testid="theater-preshow-title"
          className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6 drop-shadow-[0_2px_18px_rgba(0,0,0,0.95)]"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          {nextTitle}
        </h2>
      )}

      <div
        data-testid="theater-preshow-countdown"
        className={`mx-auto select-none font-black tabular-nums leading-none transition-transform ${
          lastBreath ? "scale-110" : ""
        }`}
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: lastBreath ? "5.5rem" : finalRush ? "4.5rem" : "3.75rem",
          background: finalRush
            ? "linear-gradient(180deg, #fff7d6 0%, #f6c34a 50%, #b8770c 100%)"
            : "linear-gradient(180deg, #f6c34a 0%, #b8770c 100%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          filter: finalRush
            ? "drop-shadow(0 0 28px rgba(246,195,74,0.85))"
            : "drop-shadow(0 0 16px rgba(246,195,74,0.4))",
          animation: finalRush ? "preshow-pulse 1s ease-in-out infinite" : undefined,
        }}
      >
        {fmt(seconds)}
      </div>

      <div className="text-[10px] uppercase tracking-[0.3em] text-[#f6c34a]/80 mt-4 mb-7">
        Curtains rise in
      </div>

      <div className="flex gap-3 justify-center">
        <button
          onClick={onRefresh}
          data-testid="theater-preshow-refresh"
          className="bg-[#E50914] hover:bg-[#b00710] transition px-5 py-2 rounded font-semibold text-sm"
        >
          Refresh
        </button>
        <button
          onClick={onGuide}
          data-testid="theater-preshow-guide"
          className="bg-white/10 hover:bg-white/20 border border-white/15 transition px-5 py-2 rounded font-semibold text-sm"
        >
          Channel guide
        </button>
      </div>

      <style>{`
        @keyframes preshow-pulse {
          0%, 100% { transform: scale(1.10); filter: drop-shadow(0 0 28px rgba(246,195,74,0.85)); }
          50%      { transform: scale(1.18); filter: drop-shadow(0 0 44px rgba(246,195,74,1.0)); }
        }
      `}</style>
    </div>
  );
}
